// Conservative health-literacy safety net for rich Journey content.
//
// The curated stage phrase lists remain the primary editorial selection. This layer
// only ensures that specialist terms actually used in rich stage copy are not left
// unexplained. Role titles are deliberately excluded because the stage People cards
// already link to the full role definition.
//
// "Reading age 12" is not mechanically reliable at single-word level, so the rules
// below use a conservative editorial proxy: acronyms, named tests/procedures,
// specialist treatment terms and NHS process jargon that a typical young reader may
// not understand without explanation.
const rule = (phraseId, pattern, journeyIds = null) => Object.freeze({
  phraseId,
  pattern,
  journeyIds: journeyIds ? Object.freeze(journeyIds) : null,
});

export const JOURNEY_TERMINOLOGY_RULES = Object.freeze([
  rule('preAssessment', /\bpre[- ]?(?:assessment|operative assessment)\b/i),
  rule('nilByMouth', /\b(?:fasting|fasted|nil by mouth|nbm|npo)\b/i),
  rule('consent', /\bconsent(?:ing)?\b/i),
  rule('anaesthesia', /\banaesthe(?:sia|tic)\b/i, ['day-surgery', 'maternity', 'surgery-operation', 'endoscopy', 'orthopaedics']),
  rule('cannula', /\bcannula\b/i),
  rule('sedation', /\bsedat(?:ion|ed)\b/i, ['critical-care-visitor']),
  rule('proceduralSedation', /\bsedat(?:ion|ed)\b/i, ['day-surgery', 'mri', 'endoscopy']),
  rule('theatre', /\b(?:operating )?theatre\b/i),
  rule('recovery', /\brecovery area\b/i),
  rule('dischargeCriteria', /\bdischarge criteria\b/i),
  rule('surgicalDrain', /\bdrains?\b/i, ['surgery-operation']),
  rule('safetyNetting', /\bsafety[- ]netting\b/i),
  rule('triage', /\btriage\b/i, ['emergency-department']),
  rule('resus', /\bresuscitation\b/i, ['emergency-department']),
  rule('ambulatoryCare', /\bambulatory care\b/i, ['emergency-department']),
  rule('admission', /\b(?:admission|admitted)\b/i),
  rule('observations', /\bobservations\b/i),
  rule('ecg', /\bECG\b/i),
  rule('monitoring', /\bmonitor(?:ing|ed)?\b/i),
  rule('clinician', /\bclinicians?\b/i),
  rule('diagnosis', /\bdiagnos(?:is|es|ed)\b/i),
  rule('investigations', /\binvestigations?\b/i),
  rule('inpatientOutpatient', /\b(?:inpatient|outpatient)s?\b/i),
  rule('acuteCare', /\bacute\b/i),
  rule('primaryCare', /\bprimary[- ]care\b/i),
  rule('carePathway', /\b(?:clinical |care |patient )?pathways?\b/i),
  rule('reasonableAdjustments', /\breasonable adjustments?\b/i, ['paediatrics']),
  rule('kidneyFunctionTests', /\b(?:creatinine|eGFR)\b/i, ['renal']),
  rule('dialysis', /\b(?:dialysis|haemodialysis|hemodialysis|peritoneal dialysis)\b/i, ['renal']),
  rule('mentalHealthAssessment', /\bmental health assessment\b/i, ['mental-health']),
  rule('riskAssessment', /\brisk assessment\b/i, ['mental-health']),
  rule('crisisPlan', /\bcrisis plan\b/i, ['mental-health']),
  rule('careCoordinator', /\bcare co-?ordinator\b/i, ['mental-health']),
  rule('confidentiality', /\bconfidentiality\b/i, ['mental-health']),
  rule('peerSupport', /\bpeer support\b/i, ['mental-health']),

  rule('antenatalCare', /\b(?:antenatal|booking appointment|screening)\b/i, ['maternity']),
  rule('maternityTriage', /\bmaternity triage\b/i, ['maternity']),
  rule('inductionOfLabour', /\binduction(?: of labour)?\b/i, ['maternity']),
  rule('caesareanSection', /\bcaesarean(?: section| birth)?\b/i, ['maternity']),
  rule('assistedBirth', /\b(?:assisted (?:vaginal )?birth|forceps|ventouse)\b/i, ['maternity']),
  rule('epidural', /\bepidural\b/i, ['maternity']),
  rule('postnatalWard', /\bpostnatal ward\b/i, ['maternity']),
  rule('neonatalUnit', /\bneonatal (?:team|unit|care|review)\b/i, ['maternity']),
  rule('neonatalIntensiveCare', /\b(?:neonatal intensive care|NICU)\b/i, ['maternity']),

  rule('mriScan', /\bMRI\b/i),
  rule('ctScan', /\bCT(?: scan)?\b/i),
  rule('contrast', /\bcontrast\b/i, ['mri', 'ct']),
  rule('claustrophobia', /\bclaustrophobi(?:a|c)\b/i, ['mri']),
  rule('resultsPending', /\bresults? (?:are )?pending\b/i),

  rule('referral', /\breferr(?:al|ed)\b/i),
  rule('wardRound', /\bward rounds?\b/i),
  rule('handover', /\bhandover\b/i),
  rule('mdt', /\bmultidisciplinary team\b/i, ['inpatient-ward', 'critical-care-visitor', 'stroke']),
  rule('criticalCare', /\bcritical[- ]care\b/i),
  rule('highDependencyUnit', /\b(?:higher[- ]dependency|high dependency)\b/i),
  rule('rehabilitation', /\brehabilitation\b/i),
  rule('prognosis', /\bprognosis\b/i, ['critical-care-visitor']),
  rule('organSupport', /\borgan support\b/i, ['critical-care-visitor']),
  rule('ventilator', /\bventilat(?:or|ed)\b/i, ['critical-care-visitor']),
  rule('vasopressors', /\bvasopressors?\b/i, ['critical-care-visitor']),
  rule('delirium', /\bdelirium\b/i, ['critical-care-visitor']),
  rule('weaning', /\bweaning\b/i, ['critical-care-visitor']),

  rule('cancerMdt', /\bmultidisciplinary team\b/i, ['cancer-care']),
  rule('biopsy', /\bbiops(?:y|ies)\b/i),
  rule('chemotherapy', /\bchemotherapy\b/i, ['cancer-care', 'chemotherapy']),
  rule('radiotherapy', /\bradiotherapy\b/i, ['cancer-care', 'radiotherapy']),
  rule('immunotherapy', /\bimmunotherapy\b/i, ['cancer-care']),
  rule('systemicAntiCancerTreatment', /\bsystemic anti[- ]cancer treatment\b/i, ['cancer-care', 'chemotherapy']),
  rule('palliativeCare', /\bpalliative\b/i, ['cancer-care']),
  rule('patientInitiatedFollowUp', /\bpatient[- ]initiated follow[- ]up\b/i, ['cancer-care']),
  rule('cancerRecurrenceSurveillance', /\b(?:recurrence|surveillance)\b/i, ['cancer-care']),

  rule('eeg', /\bEEG\b/i, ['neurology-neurosurgery']),
  rule('neurophysiologyTests', /\b(?:neurophysiology|nerve[- ]conduction)\b/i, ['neurology-neurosurgery']),
  rule('neurosurgery', /\bneurosurgery\b/i, ['neurology-neurosurgery']),

  rule('bloodGlucose', /\bblood glucose\b/i, ['stroke']),
  rule('strokeUnit', /\bstroke[- ]unit\b/i, ['stroke']),
  rule('thrombolysis', /\b(?:thrombolysis|clot[- ]busting)\b/i, ['stroke']),
  rule('thrombectomy', /\bthrombectomy\b/i, ['stroke']),
  rule('iv', /\bintravenous\b/i, ['stroke']),
  rule('swallowAssessment', /\bswallow(?:ing)?\b/i, ['stroke']),

  rule('troponinBloodTest', /\btroponin\b/i, ['cardiology']),
  rule('echocardiogram', /\bechocardiogram\b/i, ['cardiology']),
  rule('heartRhythmMonitoring', /\bheart[- ]rhythm monitoring\b/i, ['cardiology']),
  rule('angiogram', /\b(?:coronary )?angiogram\b/i, ['cardiology']),
  rule('angioplasty', /\bangioplasty\b/i, ['cardiology']),
  rule('stent', /\bstent\b/i, ['cardiology']),
  rule('cardiacRehabilitation', /\bcardiac rehabilitation\b/i, ['cardiology']),

  rule('orthopaedics', /\borthopaedic(?:s)?\b/i, ['orthopaedics']),

  rule('oxygenSaturations', /\boxygen saturations?\b/i, ['respiratory']),
  rule('spirometry', /\bspirometry\b/i, ['respiratory']),
  rule('chestXRay', /\bchest X[- ]?ray\b/i, ['respiratory']),
  rule('bloodGasTest', /\bblood gas\b/i, ['respiratory']),
  rule('oxygenTherapy', /\boxygen therapy\b/i, ['respiratory']),
  rule('nebuliser', /\bnebuliser\b/i, ['respiratory']),
  rule('cpap', /\bCPAP\b/i, ['respiratory']),
  rule('respiratoryPhysiotherapy', /\brespiratory physiotherapy\b/i, ['respiratory']),
  rule('pulmonaryRehabilitation', /\bpulmonary rehabilitation\b/i, ['respiratory']),
  rule('exacerbation', /\bexacerbation\b/i, ['respiratory']),

  rule('bowelPreparation', /\b(?:bowel[- ]cleansing preparation|bowel preparation)\b/i, ['endoscopy']),
  rule('gastroscopyColonoscopy', /\b(?:gastroscopy|colonoscopy)\b/i, ['endoscopy']),
]);

function explanationText(stage = {}) {
  const explanation = stage.explanation || {};
  const nextSteps = Array.isArray(explanation.nextSteps) ? explanation.nextSteps : [];
  return [
    explanation.whatHappens,
    explanation.whyItMayHappen,
    explanation.whatTeamIsDoing,
    explanation.variationNote,
    ...nextSteps.flatMap((step) => [step?.title, step?.description, step?.when]),
  ].filter(Boolean).join(' ');
}

export function getJourneyTerminologyPhraseIds(templateId, stage = {}) {
  const text = explanationText(stage);
  if (!text) return [];
  return JOURNEY_TERMINOLOGY_RULES
    .filter((item) => !item.journeyIds || item.journeyIds.includes(templateId))
    .filter((item) => item.pattern.test(text))
    .map((item) => item.phraseId);
}

export function applyJourneyTerminologyCoverage(templateId, stages = []) {
  return stages.map((stage) => {
    const terminologyPhraseIds = getJourneyTerminologyPhraseIds(templateId, stage);
    if (!terminologyPhraseIds.length) return stage;
    return {
      ...stage,
      phraseKeys: [...new Set([...(stage.phraseKeys || []), ...terminologyPhraseIds])],
    };
  });
}
